import PortalDocuments from './PortalDocuments';
import PortalStatus from './PortalStatus';
import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  PortalShell,
  Surface,
  Button,
  ErrorSummary,
  SectionHeader,
  LangToggle,
  useT,
  useFontClass,
  toBn,
} from "components/admissions/portalUi";
import AdmissionPrint from "components/admissions/AdmissionPrint";
import { useResource } from "hooks/useResource";
import { getInstitutions } from "services/content";
import { usePortalApplication } from "./usePortalApplication";
import {
  buildSections,
  validateSection,
  incompleteSections,
  getIn,
  setIn,
} from "./portalForm";

/**
 * Review and declaration — the last section.
 *
 * Readable cards rather than a wall of label/value rows, each with its own Edit
 * that returns straight back here afterwards. Declarations and the single
 * submit button come last, so nothing is agreed to before it has been checked.
 *
 * Submitting is idempotent server-side: a double tap, a retry after a dropped
 * connection or a reprint returns the same application rather than creating a
 * second one.
 */

function ReviewCard({ title, onEdit, children }) {
  const t = useT();
  const font = useFontClass();
  return (
    <Surface className="p-5 sm:p-6 mb-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <h2 className={`${font} text-[18px] font-bold text-green-dark`}>{title}</h2>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={`${font} text-[15px] text-green underline bg-transparent border-none cursor-pointer min-h-[44px] px-1 shrink-0`}
          >
            {t("সম্পাদনা", "Edit")}
          </button>
        )}
      </div>
      <dl className="m-0">{children}</dl>
    </Surface>
  );
}

function Row({ label, value }) {
  const font = useFontClass();
  return (
    <div className="flex flex-col sm:flex-row sm:gap-4 py-2.5 border-b border-line last:border-b-0">
      <dt className={`${font} text-[14px] text-muted sm:w-[190px] sm:shrink-0`}>{label}</dt>
      <dd className={`${font} text-[16px] text-ink m-0`}>{value || "—"}</dd>
    </div>
  );
}

/** "করিম ইসলাম / KARIM ISLAM", or whichever half was filled in. */
const pair = (bn, en) => [bn, en].filter(Boolean).join(" / ");

/** A stored date is ISO; a parent checking their own form should not see that. */
const fmtDate = (v, lang) =>
  /^\d{4}-\d{2}-\d{2}$/.test(v || "")
    ? new Date(v).toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : v;

export default function PortalReview() {
  const t = useT();
  const font = useFontClass();
  const navigate = useNavigate();

  const { refresh, app, answers, update, submit, loading, loadError, editable, access } =
    usePortalApplication();

  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  // Only used to head the printed form with the school's full name.
  const { data: institutions } = useResource(getInstitutions);

  const sections = useMemo(
    () => buildSections({ institution: app?.institution, classApplied: app?.classApplied }),
    [app]
  );

  const lang = font === "font-bn" ? "bn" : "en";
  const a = answers || {};
  const v = (path) => getIn(a, path);
  const errFor = (id) => errors.find((e) => e.id === id);

  const editSection = (key) => {
    const i = sections.findIndex((s) => s.key === key);
    if (i >= 0) navigate(`/admissions-portal/section/${i + 1}`);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Both what this screen owns, and anything left unfinished behind it — the
    // server would refuse the submit anyway, and a refusal with no explanation
    // of where to go is the worst possible answer.
    const review = sections.find((s) => s.review);
    const found = validateSection(review, a, t);
    for (const s of incompleteSections(sections, a)) {
      found.push({
        id: `section-${s.key}`,
        message: t(
          `"${s.bn}" অংশটি এখনো সম্পূর্ণ হয়নি`,
          `The "${s.en}" section is not finished yet`
        ),
      });
    }
    setErrors(found);
    if (found.length) return;

    setBusy(true);
    const { result, error } = await submit();
    setBusy(false);
    if (error) {
      setErrors([{ id: "submit-failed", message: error.message }]);
      return;
    }
    setDone(result);
  };

  if (loading) {
    return (
      <PortalShell>
        <p className={`${font} text-[16px] text-muted`}>{t("লোড হচ্ছে…", "Loading…")}</p>
      </PortalShell>
    );
  }

  if (loadError) {
    return (
      <PortalShell>
        <Surface className="p-6">
          <p className={`${font} text-[16px] text-ink mb-4`}>{loadError}</p>
          <Link to="/admissions-portal" className={`${font} text-green underline`}>
            {t("ফিরে যান", "Back to admissions")}
          </Link>
        </Surface>
      </PortalShell>
    );
  }

  /* ------------------------------------------------------------ submitted */
  if (app && !editable && !done) return <PortalStatus app={app} access={access} refresh={refresh} />;
  if (done) {
    return (
      <PortalShell>
        <div className="flex items-center justify-end mb-6">
          <LangToggle />
        </div>
        <Surface className="p-6 sm:p-8">
          <h1
            className={`${font} font-serif text-[28px] sm:text-[34px] font-bold text-green-dark leading-tight mb-3`}
          >
            {done.alreadySubmitted
              ? t("আবেদনটি আগেই জমা হয়েছে", "This application was already submitted")
              : t("আবেদন জমা হয়েছে", "Your application has been submitted")}
          </h1>
          <p className={`${font} text-[16px] text-muted leading-relaxed mb-5`}>
            {t(
              "আবেদন নম্বরটি সংরক্ষণ করুন। ফরমটি প্রিন্ট করে স্বাক্ষরসহ কাগজপত্র ও ছবি নিয়ে বিদ্যালয়ে জমা দিন।",
              "Keep your application number. Print the form, sign it, and bring it to the school with your documents and photographs."
            )}
          </p>

          <div className="bg-cream rounded-xl p-4 mb-6">
            <p className={`${font} text-[14px] text-muted mb-1`}>
              {t("আবেদন নম্বর", "Application number")}
            </p>
            <p className={`${font} text-[22px] font-bold text-green-dark tracking-wide`}>
              {done.applicationNo || app?.applicationNo}
            </p>
            {access?.resumeToken && (
              <>
                <p className={`${font} text-[14px] text-muted mt-3 mb-1`}>
                  {t("রিজিউম কোড", "Resume code")}
                </p>
                <p className={`${font} text-[15px] text-ink break-all`}>{access.resumeToken}</p>
              </>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => window.print()} className="sm:flex-1">
              {t("ফরম প্রিন্ট করুন", "Print the form")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/admissions-portal")}>
              {t("ফিরে যান", "Back to admissions")}
            </Button>
          </div>
        </Surface>

        {/*
          The two-page paper form the school still files. It is hidden on
          screen and portalled to <body>, and printing swaps to it — so what
          comes out of the printer is the form, not a screenshot of this page.
          `answers` comes from state rather than app.answers, which was loaded
          before the last save.
        */}
        <AdmissionPrint
          application={{ ...app, answers: a, status: done.status || app?.status }}
          institutionName={institutions?.items?.find((i) => i.slug === app?.institution)?.name}
        />
      </PortalShell>
    );
  }

  /* ---------------------------------------------------------------- review */
  const educationRows = v("education") || [];
  const subjects = v("subjects") || {};

  return (
    <PortalShell><PortalDocuments app={app} access={access} editable={editable} refresh={refresh} answers={answers} />
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <span className={`${font} text-[14px] text-muted`}>
          {t("আবেদন নম্বর", "Application")}{" "}
          <strong className="text-ink">{app?.applicationNo}</strong>
        </span>
        <LangToggle />
      </div>

      <SectionHeader
        step={sections.length}
        total={sections.length}
        title={t("পর্যালোচনা ও ঘোষণা", "Review and declaration")}
      />

      <p className={`${font} text-[16px] text-muted leading-relaxed mb-6`}>
        {t(
          "জমা দেওয়ার আগে সব তথ্য একবার দেখে নিন। জমা দেওয়ার পর পরিবর্তন করতে হলে বিদ্যালয়ের অনুমতি লাগবে।",
          "Please check everything before submitting. After submission, changes need the school's approval."
        )}
      </p>

      <ErrorSummary errors={errors} className="mb-6" />

      <ReviewCard title={t("শিক্ষার্থীর তথ্য", "Student")} onEdit={() => editSection("student")}>
        <Row label={t("নাম (বাংলায়)", "Name in Bangla")} value={v("applicant.nameBn")} />
        <Row label={t("নাম (ইংরেজিতে)", "Name in English")} value={v("applicant.nameEn")} />
        <Row label={t("জন্ম তারিখ", "Date of birth")} value={fmtDate(v("dob"), lang)} />
        <Row label={t("ধর্ম", "Religion")} value={v("religion")} />
        <Row label={t("জাতীয়তা", "Nationality")} value={v("nationality")} />
        <Row
          label={t("যে শ্রেণিতে আবেদন", "Applying for")}
          value={[app?.classApplied, app?.groupApplied].filter(Boolean).join(" — ")}
        />
      </ReviewCard>

      <ReviewCard title={t("পরিবার ও অভিভাবক", "Family")} onEdit={() => editSection("family")}>
        <Row label={t("পিতার নাম", "Father")} value={pair(v("father.nameBn"), v("father.nameEn"))} />
        <Row label={t("মাতার নাম", "Mother")} value={pair(v("mother.nameBn"), v("mother.nameEn"))} />
        <Row
          label={t("অভিভাবক", "Guardian")}
          value={[v("guardian.name"), v("guardian.relation"), v("guardian.occupation")]
            .filter(Boolean)
            .join(" — ")}
        />
        <Row label={t("কর্মস্থল", "Workplace")} value={v("guardian.workAddress")} />
        <Row label={t("মোবাইল নম্বর", "Mobile")} value={v("guardian.phone")} />
        <Row label={t("মাসিক আয়", "Monthly income")} value={v("guardian.monthlyIncome")} />
      </ReviewCard>

      <ReviewCard
        title={t("ঠিকানা ও শিক্ষাগত তথ্য", "Address and education")}
        onEdit={() => editSection("address")}
      >
        <Row label={t("বর্তমান ঠিকানা", "Present address")} value={v("present.address")} />
        <Row
          label={t("স্থায়ী ঠিকানা", "Permanent address")}
          value={
            v("permanent.sameAsPresent")
              ? t("বর্তমান ঠিকানার অনুরূপ", "Same as present address")
              : v("permanent.address")
          }
        />
        <Row label={t("যোগাযোগ নম্বর", "Contact number")} value={v("present.phone")} />
        {educationRows.map((row, i) => (
          <Row
            key={i}
            label={t(`পরীক্ষা ${toBn(i + 1)}`, `Examination ${i + 1}`)}
            value={[row.exam, row.groupYear, row.gpa && `GPA ${row.gpa}`, row.board]
              .filter(Boolean)
              .join(" — ")}
          />
        ))}
      </ReviewCard>

      {sections.some((s) => s.key === "subjects") && (
        <ReviewCard title={t("বিষয় নির্বাচন", "Subjects")} onEdit={() => editSection("subjects")}>
          <Row
            label={t("আবশ্যিক", "Compulsory")}
            value={(subjects.compulsory || []).filter(Boolean).join(", ")}
          />
          <Row
            label={t("ঐচ্ছিক", "Elective")}
            value={(subjects.optional || []).filter(Boolean).join(", ")}
          />
        </ReviewCard>
      )}

      {/* Declarations last, immediately above the single submit. */}
      <form onSubmit={onSubmit} noValidate>
        <Surface className="p-5 sm:p-6 mb-6 mt-7">
          <h2 className={`${font} text-[18px] font-bold text-green-dark mb-4`}>
            {t("ঘোষণা", "Declarations")}
          </h2>

          {[
            {
              id: "declarations.applicant",
              text: t(
                "আমি অঙ্গীকার করছি যে, ভর্তি হলে প্রতিষ্ঠানের সকল নিয়ম-কানুন মেনে চলব এবং নিয়মিত ক্লাসে উপস্থিত থাকব।",
                "I undertake that, if admitted, I will follow all the institution's rules and attend classes regularly."
              ),
            },
            {
              id: "declarations.guardian",
              text: t(
                "অভিভাবক হিসেবে আমি তার বেতনাদি নিয়মিত পরিশোধ করব এবং প্রতিষ্ঠানের সকল সিদ্ধান্ত মেনে নেব।",
                "As guardian I will pay the fees regularly and accept the institution's decisions."
              ),
            },
          ].map((d) => (
            <label
              key={d.id}
              htmlFor={d.id}
              className={`flex gap-3.5 items-start rounded-xl p-3.5 mb-3 cursor-pointer border-[1.5px] transition-colors ${
                errFor(d.id) ? "border-red bg-[#fdf6f6]" : "border-line hover:bg-cream"
              }`}
            >
              <input
                id={d.id}
                type="checkbox"
                disabled={!editable}
                checked={!!v(d.id)}
                onChange={(e) => update(setIn(a, d.id, e.target.checked))}
                aria-invalid={errFor(d.id) ? "true" : undefined}
                className="mt-1 w-5 h-5 cursor-pointer shrink-0 accent-[#005C40]"
              />
              <span className={`${font} text-[15.5px] text-ink leading-relaxed`}>{d.text}</span>
            </label>
          ))}

          <p className={`${font} text-[14px] text-muted leading-relaxed mt-4`}>
            {t(
              "জমা দেওয়ার পর আপনি একটি আবেদন নম্বর ও প্রিন্টযোগ্য ফরম পাবেন। ফরমটি প্রিন্ট করে স্বাক্ষরসহ কাগজপত্র ও ছবি নিয়ে বিদ্যালয়ে জমা দিন।",
              "After submitting you will get an application number and a printable form. Print it, sign it, and bring it to the school with your documents and photographs."
            )}
          </p>
        </Surface>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:gap-4">
          <Button
            type="button"
            variant="secondary"
            className="sm:w-auto"
            disabled={busy}
            onClick={() => navigate(`/admissions-portal/section/${sections.length - 1}`)}
          >
            ← {t("পূর্ববর্তী", "Back")}
          </Button>
          <Button type="submit" className="sm:flex-1" disabled={busy || !editable}>
            {busy ? t("জমা হচ্ছে…", "Submitting…") : t("আবেদন জমা দিন", "Submit application")}
          </Button>
        </div>
      </form>
    </PortalShell>
  );
}
